// Turning the portfolio into sheet rows.
//
// Pure on purpose: the column list, the two calculated columns and the empty-column pruning
// are the parts worth testing, and none of them need SheetJS. The workbook assembly lives
// next door in portfolioWorkbook.ts and is a handful of library calls.
//
// Column names and order come from the client's own report — Sheet1 of
// "Purchase and Liquidation Report - tabs.xlsx", the one layout of the three that has real
// data in it. See docs/reports-design.md for why the other two are not layouts to match.

import type { Loan, LoanNote, LoanRequest } from '@app/shared';

export type Cell = string | number | boolean | null;
export interface Sheet {
  name: string;
  headers: string[];
  rows: Cell[][];
}

const yn = (v: boolean | null | undefined) => (v == null ? null : v ? 'Yes' : 'No');

/** Whole years since a date, to one decimal — the layout's "Time Since Purchased (YRS.)". */
export function yearsSince(from: string | null, today: string): number | null {
  if (!from) return null;
  const then = Date.parse(from);
  const now = Date.parse(today);
  if (Number.isNaN(then) || Number.isNaN(now)) return null;
  return Math.round(((now - then) / 31_557_600_000) * 10) / 10;
}

/** Whole days a payment is overdue — the layout's "Days Past Due", marked (Calc) there too. */
export function daysPastDue(nextDueDate: string | null, today: string): number | null {
  if (!nextDueDate) return null;
  const due = Date.parse(nextDueDate);
  const now = Date.parse(today);
  if (Number.isNaN(due) || Number.isNaN(now)) return null;
  const days = Math.floor((now - due) / 86_400_000);
  // Not yet due is not "negative days past due"; it is not past due.
  return days > 0 ? days : null;
}

/**
 * Drop columns that are empty in every row.
 *
 * Nine of the layout's columns can only be filled by someone typing into the Overview, and
 * none of them are in the monthly extract — so on freshly imported data a faithful report is
 * a quarter empty (docs/reports-design.md §3). Pruning is by emptiness rather than by a
 * hardcoded list of "columns we cannot fill", because those columns *can* be filled: the
 * moment somebody enters a Legal Balance the column appears, with no code change.
 *
 * The cost is that the file's shape depends on its contents, so two exports may not have the
 * same columns. That is the right trade for a file a person opens and reads; it would be the
 * wrong one for a file pasted into a fixed template, and nobody has said it is.
 */
export function pruneEmptyColumns(sheet: Sheet): Sheet {
  const keep = sheet.headers.map((_, i) =>
    sheet.rows.some(r => r[i] !== null && r[i] !== undefined && r[i] !== ''));
  // An empty sheet keeps its headers: a file with no columns explains nothing.
  if (sheet.rows.length === 0 || keep.every(Boolean)) return sheet;
  return {
    name: sheet.name,
    headers: sheet.headers.filter((_, i) => keep[i]),
    rows: sheet.rows.map(r => r.filter((_, i) => keep[i])),
  };
}

// ---------------------------------------------------------------------------
// Sheet 1 — one row per loan
// ---------------------------------------------------------------------------

const LOAN_COLUMNS: { header: string; value: (l: Loan, ctx: Ctx) => Cell }[] = [
  { header: 'SA Officer', value: l => l.assignedSaOfficerName },
  { header: 'SBA Loan Number', value: l => l.sbaLoanNumber },
  { header: 'UMWSB Loan #', value: l => l.umwsbLoanNumber },
  { header: 'Borrower Name', value: l => l.borrowerName },
  { header: 'State Located', value: l => l.stateLocated },
  { header: 'NAICS Code Description', value: l => l.naicsDescription },
  { header: 'Approval Gross', value: l => l.approvalGross },
  { header: 'Gty %', value: l => l.guarantyPct },
  { header: 'Date 1st Disbursed', value: l => l.dateFirstDisbursed },
  { header: 'Maturity Date', value: l => l.maturityDate },
  { header: 'In Business', value: l => yn(l.inBusiness) },
  { header: 'SBA Loan Status', value: l => l.sbaLoanStatus },
  // Not in the layout, and included anyway: it is the status the app actually derives and
  // the one users read on the grid, so a report without it describes a different system.
  { header: 'Line Status', value: l => l.lineStatus },
  { header: 'Legal Balance', value: l => l.legalBalance },
  { header: 'Next Due Date', value: l => l.nextDueDate },
  { header: 'Days Past Due', value: (l, c) => daysPastDue(l.nextDueDate, c.today) },
  { header: 'Date of Default', value: l => l.dateOfDefault },
  { header: 'Date of Initial Site Visit', value: l => l.dateInitialSiteVisit },
  { header: 'Approved Written Plan', value: l => yn(l.approvedWrittenPlan) },
  { header: 'Purchase Date', value: l => l.purchaseDate },
  { header: 'Principal Balance at Purchase', value: l => l.principalBalanceAtPurchase },
  { header: 'Time Since Purchased (YRS.)', value: (l, c) => yearsSince(l.purchaseDate, c.today) },
  { header: 'UMWSB Charged Off Date', value: l => l.chargedOffDate },
  { header: 'Active BK or Litigation', value: l => yn(l.activeBkOrLitigation) },
  { header: 'Collateral in Liquidation', value: l => yn(l.collateralInLiquidation) },
  { header: 'Date to SBA Liquidation Status', value: l => l.dateToSbaLiquidation },
  { header: 'Date Demand Letter Sent', value: l => l.dateDemandLetterSent },
  { header: 'Date Charge Off Submitted', value: l => l.dateChargeOffSubmitted },
  { header: 'Date Extension Requested', value: l => l.dateExtensionRequested },
  { header: 'Date Extension Approval', value: l => l.dateExtensionApproval },
  { header: 'Date Extension Expires', value: l => l.dateExtensionExpires },
  { header: 'Accrued Expenses (SBA Receivable)', value: l => l.accruedExpenses },
  { header: 'Date of Final SBA Charge Off', value: l => l.dateFinalSbaChargeOff },
  { header: 'Date of SBA Uncollectible', value: l => l.dateSbaUncollectible },
  { header: 'Early Default', value: l => yn(l.earlyDefault) },
  { header: 'Repair/Denial Pending', value: l => yn(l.repairDenialPending) },
  { header: 'Next Action Date', value: l => l.nextActionDate },
  { header: 'Action', value: l => l.action },
  // The layout has one Notes cell per loan, and in the sample it holds three years of
  // narrative. Kept, because dropping it would lose the column the client plainly uses most
  // — newest first, so the useful end is the readable end of a very tall cell.
  { header: 'Notes', value: (l, c) => c.notesByLoan.get(l.id) ?? null },
];

interface Ctx {
  today: string;
  notesByLoan: Map<number, string>;
}

/**
 * `today` is passed in rather than read from the clock so the calculated columns are
 * reproducible and testable.
 */
export function loanSheet(loans: Loan[], notes: LoanNote[], today: string): Sheet {
  const notesByLoan = new Map<number, string>();
  const sorted = [...notes].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  for (const n of sorted) {
    if (n.isDeleted) continue;
    const line = `[${n.createdAt.slice(0, 10)}${n.createdByName ? ` ${n.createdByName}` : ''}] ${n.noteText}`;
    const prev = notesByLoan.get(n.loanId);
    notesByLoan.set(n.loanId, prev ? `${prev}\n\n${line}` : line);
  }
  const ctx: Ctx = { today, notesByLoan };
  return pruneEmptyColumns({
    name: 'Loans',
    headers: LOAN_COLUMNS.map(c => c.header),
    // ?? null because a Loan field the API omitted arrives as undefined, and Cell does not
    // include undefined — leaving it would make the declared type a near-miss.
    rows: loans.map(l => LOAN_COLUMNS.map(c => c.value(l, ctx) ?? null)),
  });
}

// ---------------------------------------------------------------------------
// Sheet 2 — one row per package
// ---------------------------------------------------------------------------

/**
 * Every package, so the history the flat sheet cannot hold is somewhere.
 *
 * The loan carries only the *latest* of each mirrored date, so a loan with three Extensions
 * contributes one row to the Loans sheet and three here. Cancelled and held packages are
 * included: what was abandoned is part of the history, and a report that quietly omitted it
 * would be the one place the audit trail disagreed with itself.
 *
 * Package fields are emitted as one column per key actually present across the set, rather
 * than every key any package type could carry — otherwise a portfolio of Purchases gets
 * forty empty OIC and litigation columns.
 */
export function packageSheet(packages: LoanRequest[]): Sheet {
  const fieldKeys: string[] = [];
  for (const p of packages) {
    for (const [k, v] of Object.entries(p.fields ?? {})) {
      if (v === null || v === undefined || v === '' || v === false) continue;
      if (!fieldKeys.includes(k)) fieldKeys.push(k);
    }
  }
  fieldKeys.sort();

  const headers = [
    'SBA Loan Number', 'Borrower Name', 'Package Type', 'Status',
    'Submitted By', 'Submitted At', 'Closed At', 'Closed By', 'Comments',
    ...fieldKeys,
  ];
  const rows = packages.map(p => [
    p.sbaLoanNumber, p.borrowerName, p.requestType, p.status,
    p.submittedByName, p.submittedAt?.slice(0, 10) ?? null,
    p.closedAt?.slice(0, 10) ?? null, p.closedByName, p.description,
    ...fieldKeys.map(k => {
      const v = (p.fields as Record<string, unknown>)?.[k];
      if (v === null || v === undefined) return null;
      if (typeof v === 'boolean') return v ? 'Yes' : 'No';
      return v as Cell;
    }),
  ]);
  return { name: 'Packages', headers, rows };
}

// ---------------------------------------------------------------------------
// Sheet 3 — one row per note
// ---------------------------------------------------------------------------

/**
 * Notes one per row, which is the only shape in which they are sortable or searchable.
 * The Loans sheet has them concatenated into a single cell, which is what the client's own
 * report does and is unusable for anything but reading.
 */
export function noteSheet(notes: LoanNote[]): Sheet {
  return {
    name: 'Notes',
    headers: ['SBA Loan Number', 'Borrower Name', 'Date', 'Author', 'Note'],
    rows: notes.filter(n => !n.isDeleted).map(n => {
      const withLoan = n as LoanNote & { sbaLoanNumber?: string; borrowerName?: string };
      return [
        withLoan.sbaLoanNumber ?? null, withLoan.borrowerName ?? null,
        n.createdAt.slice(0, 10), n.createdByName, n.noteText,
      ];
    }),
  };
}
